import type { GroupEvent } from '@branchjs/neo-components';

import {
  CALCULATED_ECPC,
  CALCULATED_ECPI,
  CALCULATED_ECPM,
  CLICK_TO_IMPRESSION,
  DRUID_TO_HUMAN_READABLE,
  EO_CLICK_TO_EO_INSTALL,
  EO_CLICK_TO_EO_REINSTALL,
} from 'client/js/common/support/eo_mapping';
import {
  EO_BRANCH_CTA_VIEW,
  EO_CLICK,
  EO_DISMISSAL,
  EO_IMPRESSION,
  EO_INSTALL,
  EO_OPEN,
  EO_REINSTALL,
  EO_SMS_SENT,
  EO_WEB_SESSION_START,
} from 'client/js/common/support/eo_mapping/datasources';
import {
  CONTENT_EVENT_NAMES,
  NAMED_COMMERCE_EVENT_NAMES,
  PURCHASE,
  USER_LIFECYCLE_EVENT_NAMES,
} from 'client/js/common/support/eo_mapping/event_names';
import {
  CALCULATION_DATA_FIELD_KEYS,
  COST,
  DATA_FIELD_KEYS,
  DYNAMIC_CALCULATION_DATA_FIELD_KEY_PREFIX,
  EVENT_DATA_FIELD_KEYS,
  REVENUE,
} from '../../../../../shared/constants';
import { REPORT_COLUMN_KEYS } from '../../../../../shared/constants/dataFieldKeys/reportColumnKeys';
import {
  CALCULATION_ID_EVENTS,
  COST_PER,
  CUSTOM_METRICS_EVENT_NAME,
  DEFAULT_COLUMN_GROUP_PROPS,
  RATE_FOR,
  generateDynamicCalculationKey,
} from '../../../../../shared/constants/dynamicMetrics';

export const IGNORED_EVENTS = [COST, REVENUE, EO_INSTALL, EO_OPEN, EO_WEB_SESSION_START, PURCHASE];

export const EVENT_GROUPS_DELIVERY: GroupEvent[] = [
  {
    name: DRUID_TO_HUMAN_READABLE[COST],
    order: 0,
    columns: [
      {
        ...DEFAULT_COLUMN_GROUP_PROPS,
        label: DRUID_TO_HUMAN_READABLE[COST],
        value: COST,
      },
    ],
  },
  {
    name: DRUID_TO_HUMAN_READABLE[EO_IMPRESSION],
    order: 1,
    columns: [
      {
        ...DEFAULT_COLUMN_GROUP_PROPS,
        label: DRUID_TO_HUMAN_READABLE[EO_IMPRESSION],
        value: DATA_FIELD_KEYS.IMPRESSION,
      },
      {
        ...DEFAULT_COLUMN_GROUP_PROPS,
        label: DRUID_TO_HUMAN_READABLE[CALCULATED_ECPM],
        value: CALCULATION_DATA_FIELD_KEYS.ECPM,
      },
    ],
  },
  {
    name: DRUID_TO_HUMAN_READABLE[EO_CLICK],
    columns: [
      {
        ...DEFAULT_COLUMN_GROUP_PROPS,
        label: DRUID_TO_HUMAN_READABLE[EO_CLICK],
        value: DATA_FIELD_KEYS.CLICK,
      },
      {
        ...DEFAULT_COLUMN_GROUP_PROPS,
        label: DRUID_TO_HUMAN_READABLE[CALCULATED_ECPC],
        value: CALCULATION_DATA_FIELD_KEYS.ECPC,
      },
      {
        ...DEFAULT_COLUMN_GROUP_PROPS,
        label: DRUID_TO_HUMAN_READABLE[CLICK_TO_IMPRESSION],
        value: CALCULATION_DATA_FIELD_KEYS.CLICK_TO_IMPRESSION,
      },
    ],
  },
  {
    name: DRUID_TO_HUMAN_READABLE[EO_INSTALL],
    columns: [
      {
        ...DEFAULT_COLUMN_GROUP_PROPS,
        label: DRUID_TO_HUMAN_READABLE[EO_INSTALL],
        value: EVENT_DATA_FIELD_KEYS.INSTALL,
      },
      {
        ...DEFAULT_COLUMN_GROUP_PROPS,
        label: DRUID_TO_HUMAN_READABLE[CALCULATED_ECPI],
        value: CALCULATION_DATA_FIELD_KEYS.ECPI,
      },
      {
        ...DEFAULT_COLUMN_GROUP_PROPS,
        label: DRUID_TO_HUMAN_READABLE[EO_CLICK_TO_EO_INSTALL],
        value: CALCULATION_DATA_FIELD_KEYS.CLICK_TO_INSTALL,
      },
      {
        ...DEFAULT_COLUMN_GROUP_PROPS,
        label: DRUID_TO_HUMAN_READABLE[EO_REINSTALL],
        value: EVENT_DATA_FIELD_KEYS.REINSTALL,
      },
      {
        ...DEFAULT_COLUMN_GROUP_PROPS,
        label: DRUID_TO_HUMAN_READABLE[EO_CLICK_TO_EO_REINSTALL],
        value: CALCULATION_DATA_FIELD_KEYS.CLICK_TO_REINSTALL,
      },
    ],
  },
  {
    name: DRUID_TO_HUMAN_READABLE[EO_OPEN],
    columns: [
      {
        ...DEFAULT_COLUMN_GROUP_PROPS,
        label: DRUID_TO_HUMAN_READABLE[EO_OPEN],
        value: EVENT_DATA_FIELD_KEYS.OPEN,
      },
      {
        ...DEFAULT_COLUMN_GROUP_PROPS,
        label: `cost per ${DRUID_TO_HUMAN_READABLE[EO_OPEN]}`,
        value: generateDynamicCalculationKey(COST_PER, EVENT_DATA_FIELD_KEYS.OPEN),
      },
      {
        ...DEFAULT_COLUMN_GROUP_PROPS,
        label: `${DRUID_TO_HUMAN_READABLE[EO_OPEN]} rate`,
        value: generateDynamicCalculationKey(RATE_FOR, EVENT_DATA_FIELD_KEYS.OPEN),
      },
    ],
  },
  {
    name: DRUID_TO_HUMAN_READABLE[EO_WEB_SESSION_START],
    columns: [
      {
        ...DEFAULT_COLUMN_GROUP_PROPS,
        label: DRUID_TO_HUMAN_READABLE[EO_WEB_SESSION_START],
        value: EVENT_DATA_FIELD_KEYS.WEB_SESSION_START,
      },
      {
        ...DEFAULT_COLUMN_GROUP_PROPS,
        label: `cost per ${DRUID_TO_HUMAN_READABLE[EO_WEB_SESSION_START]}`,
        value: generateDynamicCalculationKey(COST_PER, EVENT_DATA_FIELD_KEYS.WEB_SESSION_START),
      },
      {
        ...DEFAULT_COLUMN_GROUP_PROPS,
        label: `click to ${DRUID_TO_HUMAN_READABLE[EO_WEB_SESSION_START]}`,
        value: CALCULATION_DATA_FIELD_KEYS.CLICK_TO_WEB_SESSION_START,
      },
      {
        ...DEFAULT_COLUMN_GROUP_PROPS,
        label: `${DRUID_TO_HUMAN_READABLE[EO_WEB_SESSION_START]} rate`,
        value: generateDynamicCalculationKey(RATE_FOR, EVENT_DATA_FIELD_KEYS.WEB_SESSION_START),
      },
    ],
  },
];

export const SUPPORTED_DYNAMIC_EVENTS = [
  COST,
  REVENUE,
  EO_INSTALL,
  EO_OPEN,
  EO_SMS_SENT,
  EO_BRANCH_CTA_VIEW,
  EO_WEB_SESSION_START,
  EO_DISMISSAL,
  CUSTOM_METRICS_EVENT_NAME,
  ...Object.keys(NAMED_COMMERCE_EVENT_NAMES),
  ...Object.keys(USER_LIFECYCLE_EVENT_NAMES),
  ...Object.keys(CONTENT_EVENT_NAMES),
];

export const buildDynamicEventList = (): string[] => {
  const list: string[] = [];

  for (const calculationId of Object.keys(CALCULATION_ID_EVENTS)) {
    for (const event of SUPPORTED_DYNAMIC_EVENTS) {
      list.push(`${DYNAMIC_CALCULATION_DATA_FIELD_KEY_PREFIX}${calculationId}__${REPORT_COLUMN_KEYS[event]}`);
    }
  }

  return list;
};
